/**
 * Route wrapper for hub pages (Warehouse, Production) that nest several child
 * pages. Grants access when the user is permitted for AT LEAST ONE of the
 * given child page keys — mirroring the hub *tab* visibility rule in
 * FieldTabBar (useAnyPagePermission).
 *
 * Behavior matches RequirePermission:
 *   - Unauthenticated → redirect to /field/login, remembering the target URL.
 *   - Authenticated but permitted for none of the children → redirect to
 *     /field/home with a toast.
 *   - Loading → tiny inline spinner; the prior page isn't unmounted.
 *
 * The hub page's individual buttons still gate themselves per child pageKey,
 * so reaching the hub never exposes an action the role can't perform.
 */

import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { notifications } from '../../../config/toast-notifications.js'

import { useAnyPagePermission, type FieldPageKey } from './usePagePermission.js'

interface RequireAnyPermissionProps {
  pageKeys: readonly FieldPageKey[]
  children: ReactNode
}

export function RequireAnyPermission({ pageKeys, children }: RequireAnyPermissionProps) {
  const location = useLocation()
  const { allowed, isLoading, isUnauthenticated } = useAnyPagePermission(pageKeys)

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 text-sm text-muted-foreground">
        Checking permissions…
      </div>
    )
  }

  if (isUnauthenticated) {
    return <Navigate to="/field/login" state={{ from: location.pathname + location.search }} replace />
  }

  if (!allowed) {
    notifications.info('Not available', "You don't have access to that page.")
    return <Navigate to="/field/home" replace />
  }

  return <>{children}</>
}
